/**
 * Owner: Masa Bayu
 * File: author-earnings.ts
 * Date:Time: 2026-09-16 14:52
 * Description: T6-E5 EarningLedger from SaleRecords; PayoutRequest intent only.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeAuthorSurfaceAccess } from './author-account';
import { assertT6E1SecurityIntact, assertT6E4SecurityIntact, assertT6E5SecurityIntact, } from './author-security';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
export const QXREAD_EARNING_LEDGER_STATES = [
    'pending',
    'available',
    'payout_requested',
    'paid',
];
export const QXREAD_PAYOUT_REQUEST_STATES = ['requested', 'processing', 'paid', 'failed'];
function assertLedgerState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_EARNING_LEDGER_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_EARNING_LEDGER_STATES.join(', ')}`);
    }
    return value;
}
function assertPayoutState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_PAYOUT_REQUEST_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_PAYOUT_REQUEST_STATES.join(', ')}`);
    }
    return value;
}
function assertNonNegativeInt(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(`${label} must be a non-negative integer`);
    }
    return value;
}
function assertPositiveInt(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
        throw new Error(`${label} must be a positive integer`);
    }
    return value;
}
/** Project ledger lines from author-owned SaleRecords only — void sales excluded. */
export function projectEarningLedgerFromSaleRecords(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead earning ledger');
    assertT6E1SecurityIntact();
    assertT6E4SecurityIntact();
    assertT6E5SecurityIntact();
    assertValidId(input.authorAccount.authorAccountId, 'QXRead authorAccountId');
    const updatedAt = input.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead earning updatedAt');
    const entries = [];
    for (const sale of input.saleRecords) {
        if (sale.authorAccountId !== input.authorAccount.authorAccountId) {
            continue;
        }
        if (sale.state === 'void') {
            continue;
        }
        entries.push(Object.freeze({
            earningEntryId: createQxreadId('qxrdearn'),
            authorAccountId: input.authorAccount.authorAccountId,
            accountId: input.authorAccount.accountId,
            saleRecordId: sale.saleRecordId,
            orderId: sale.orderId,
            amountMinorUnits: assertNonNegativeInt(sale.sellerSubtotalMinorUnits, 'QXRead earning amountMinorUnits'),
            currency: sale.currency,
            state: assertLedgerState('pending', 'QXRead earning state'),
            updatedAt: sale.recordedAt,
            derivedFromSaleRecord: true,
            inventedByAuthorUi: false,
        }));
    }
    return Object.freeze(entries);
}
/** Commerce settlement marks pending earnings available — not callable from Author UI authority. */
export function applyCommerceEarningSettlement(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead earning settlement');
    assertT6E5SecurityIntact();
    const settledAt = input.settledAt ?? new Date().toISOString();
    assertTimestamp(settledAt, 'QXRead earning settledAt');
    return Object.freeze(input.ledgerEntries.map((entry) => {
        if (entry.state !== 'pending') {
            return entry;
        }
        return Object.freeze({
            ...entry,
            state: assertLedgerState('available', 'QXRead earning state'),
            updatedAt: settledAt,
        });
    }));
}
/**
 * Author payout intent — requires available balance and step-up verification.
 * Does not execute Stripe payout (Commerce boundary).
 */
export function createAuthorPayoutRequest(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead payout request');
    assertT6E5SecurityIntact();
    assertValidId(input.authorAccount.authorAccountId, 'QXRead authorAccountId');
    if (!input.stepUpVerified) {
        throw new Error('QXRead payout request requires step-up verification');
    }
    const amountMinorUnits = assertPositiveInt(input.amountMinorUnits, 'QXRead payout amountMinorUnits');
    const requestedAt = input.requestedAt ?? new Date().toISOString();
    assertTimestamp(requestedAt, 'QXRead payout requestedAt');
    const available = input.ledgerEntries.filter((entry) => entry.authorAccountId === input.authorAccount.authorAccountId && entry.state === 'available');
    let availableTotal = 0;
    let currency = 'USD';
    for (const entry of available) {
        availableTotal += entry.amountMinorUnits;
        currency = entry.currency;
    }
    if (amountMinorUnits > availableTotal) {
        throw new Error('QXRead payout amount exceeds available earnings balance');
    }
    const selectedIds = [];
    let allocated = 0;
    const entryIdsToMark = new Set();
    for (const entry of available) {
        if (allocated >= amountMinorUnits) {
            break;
        }
        selectedIds.push(entry.earningEntryId);
        entryIdsToMark.add(entry.earningEntryId);
        allocated += entry.amountMinorUnits;
    }
    if (allocated !== amountMinorUnits) {
        throw new Error('QXRead payout amount must match an exact sum of available ledger entries (no partial splits in E5)');
    }
    const updatedEntries = input.ledgerEntries.map((entry) => {
        if (!entryIdsToMark.has(entry.earningEntryId)) {
            return entry;
        }
        return Object.freeze({
            ...entry,
            state: assertLedgerState('payout_requested', 'QXRead earning state'),
            updatedAt: requestedAt,
        });
    });
    const payoutRequest = Object.freeze({
        payoutRequestId: createQxreadId('qxrdpay'),
        authorAccountId: input.authorAccount.authorAccountId,
        accountId: input.authorAccount.accountId,
        amountMinorUnits,
        currency,
        state: assertPayoutState('requested', 'QXRead payout state'),
        earningEntryIds: Object.freeze(selectedIds),
        requestedAt,
        stepUpVerified: true,
        executesPayoutInAuthorUi: false,
        stripePayoutUnderCommerce: true,
    });
    return Object.freeze({ payoutRequest, ledgerEntries: Object.freeze(updatedEntries) });
}
export function projectAuthorEarningsSummary(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead earnings summary');
    assertT6E5SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    const projectedAt = input.projectedAt ?? new Date().toISOString();
    assertTimestamp(projectedAt, 'QXRead earnings projectedAt');
    const owned = input.ledgerEntries.filter((entry) => entry.authorAccountId === input.authorAccount.authorAccountId);
    let pendingMinorUnits = 0;
    let availableMinorUnits = 0;
    let payoutRequestedMinorUnits = 0;
    let paidMinorUnits = 0;
    let currency = typeof input.currency === 'string' && input.currency.length > 0 ? input.currency : 'USD';
    for (const entry of owned) {
        currency = entry.currency;
        switch (entry.state) {
            case 'pending':
                pendingMinorUnits += entry.amountMinorUnits;
                break;
            case 'available':
                availableMinorUnits += entry.amountMinorUnits;
                break;
            case 'payout_requested':
                payoutRequestedMinorUnits += entry.amountMinorUnits;
                break;
            case 'paid':
                paidMinorUnits += entry.amountMinorUnits;
                break;
            default:
                break;
        }
    }
    return Object.freeze({
        authorAccountId: input.authorAccount.authorAccountId,
        accountId: input.authorAccount.accountId,
        pendingMinorUnits,
        availableMinorUnits,
        payoutRequestedMinorUnits,
        paidMinorUnits,
        currency,
        entryCount: owned.length,
        projectedAt,
        accessAllowed: access.allowed,
        accessReason: access.reason,
    });
}
//# sourceMappingURL=author-earnings.js.map